/**
 * ActingTenantBanner — a PERSISTENT, visually distinct banner shown whenever the
 * user is acting inside a MANAGED (delegated) account. Makes the current scope
 * unmistakable (Loi-25: the user must always know whose data they are touching)
 * and offers a one-click return to their own account.
 *
 * Renders nothing when acting in the user's own account.
 */

import { useActingTenant } from '@/app/providers/ActingTenantProvider';
import { delegatedAccountName } from '@/lib/api/delegations';
import { useT } from '@/i18n';

export function ActingTenantBanner() {
  const { t } = useT();
  const { account, clear } = useActingTenant();

  if (!account) return null;

  const back = () => {
    clear();
    // Re-fetch everything back in the user's own scope.
    window.location.reload();
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="acting-tenant-banner"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-warning/40 bg-warning/15 px-4 py-2 text-sm text-foreground"
    >
      <span aria-hidden="true" className="text-base leading-none">
        👁️
      </span>
      <span className="min-w-0 flex-1">{t('shell.app.layoutsActingTenantBanner.youAreActingInTheAccount')}{' '}
        <span className="font-semibold">{delegatedAccountName(account)}</span>{t('shell.app.layoutsActingTenantBanner.accessLimitedToTheGrantedPermissions')}</span>
      <button
        type="button"
        onClick={back}
        className="shrink-0 rounded-token border border-warning/50 bg-surface px-3 py-1 font-medium text-foreground transition-colors hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-ring"
      >{t('shell.app.layoutsActingTenantBanner.backToMyAccount')}</button>
    </div>
  );
}
